import {Component, inject, Inject, OnInit} from '@angular/core';
import {DialogBaseComponent} from "../dialog-base/dialog-base.component";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ActiveUserService} from "../../../services/active-user.service";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {SnackbarService} from "../../../services/snackbar.service";
import {IUploadImageToAzure} from "../../../interfaces/IUploadImageToAzure";
import {GameThemeType} from "../../../enum/GameThemeType";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect} from "@angular/material/select";
import {MatToolbar} from "@angular/material/toolbar";
import {AssetManagementService} from "../../../services/asset-management.service";
import {GameService} from "../../../services/game.service";
import {concat, Observable, throwError} from "rxjs";
import {catchError, last, tap} from "rxjs/operators";
import {IApiErrorResponse} from "../../../interfaces/error/IApiErrorResponse";

@Component({
  selector: 'app-edit-game-image',
  standalone: true,
  imports: [
    DialogBaseComponent,
    FormsModule,
    MatButton,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatLabel,
    MatOption,
    MatSelect,
    MatToolbar,
    ReactiveFormsModule
  ],
  templateUrl: './edit-game-image.component.html',
  styleUrl: './edit-game-image.component.css'
})
export class EditGameImageComponent implements OnInit {

  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private snackBarService: SnackbarService = inject(SnackbarService);
  public assetManagementService: AssetManagementService = inject(AssetManagementService);
  private gameService: GameService = inject(GameService);

  public uiState = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;
  public gameThemeType = GameThemeType;
  public selectedTheme!: GameThemeType;

  constructor(private dialogRef: MatDialogRef<EditGameImageComponent>,
              @Inject(MAT_DIALOG_DATA) public game: IGameQueryResult) {
  }

  ngOnInit() {
    this.assetManagementService.clearGameLogo();
    this.assetManagementService.checkForExistingGameLogo(this.game.Subdomain);
    this.selectedTheme = this.game.GameThemeTypeId;
    this.assetManagementService.handleThemeTypeChange(this.selectedTheme);
  }

  onGameLogoFileSelected(event: any) {
    this.assetManagementService.handleGameLogoSelected(event)
  }

  onThemeTypeChange(valueP: GameThemeType) {
    this.selectedTheme = valueP;
    this.assetManagementService.handleThemeTypeChange(valueP);
  }

  saveGameLogo() {
    const saves: Observable<unknown>[] = [];
    const theme_to_save = this.selectedTheme;

    if (theme_to_save !== this.game.GameThemeTypeId) {
      saves.push(this.gameService.updateGameThemeType({
        GameId: this.game.Id,
        AdminId: this.activeUserService.activeUser().Id,
        GameThemeTypeId: theme_to_save
      }).pipe(
        tap(() => this.game.GameThemeTypeId = theme_to_save),
        catchError((errP: IApiErrorResponse) => throwError(() => ({error: errP, fallback: 'Failed to save the game theme.'})))
      ));
    }

    if (this.assetManagementService.gameLogoImage) {
      const formData = new FormData();
      formData.append('file', this.assetManagementService.gameLogoImage, 'game-logo.svg');

      const new_logo_request: IUploadImageToAzure = {
        gameId: this.game.Id,
        image: formData,
        adminId: this.activeUserService.activeUser().Id
      };
      saves.push(this.assetManagementService.uploadGameImage(new_logo_request).pipe(
        catchError((errP: IApiErrorResponse) => throwError(() => ({error: errP, fallback: 'Failed to upload the game logo.'})))
      ));
    }

    if (saves.length === 0) {
      this.dialogRef.close(false);
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    concat(...saves).pipe(last()).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
        this.assetManagementService.clearGameLogo();
        this.snackBarService.openSuccessfulSnackBar('Successfully saved the game logo settings.');
        this.dialogRef.close(true);
      },
      error: (errP: {error: IApiErrorResponse, fallback: string}) => {
        this.uiState = UIStateEnum.ShowData;
        if (errP.error?.Error?.ClientErrorMessages?.length > 0) {
          this.snackBarService.openErrorSnackBar(errP.error.Error.ClientErrorMessages.toString());
        } else {
          this.snackBarService.openErrorSnackBar(errP.fallback);
        }
      }
    });
  }
}
